import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL, getAuthHeader } from './leadsConstants';
import { sendWhatsApp } from '@/utils/whatsappHelper';

export function WhatsAppModal({ lead, onClose }) {
  const [templates, setTemplates] = React.useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState('');
  const [messageData, setMessageData] = React.useState({ body: '', whatsapp_url: '' });
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/templates?template_type=whatsapp`, getAuthHeader());
      setTemplates(response.data);
      if (response.data.length > 0) {
        setSelectedTemplateId(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load templates');
    }
    setLoading(false);
  };

  React.useEffect(() => {
    if (selectedTemplateId && lead.id) {
      loadTemplate();
    }
  }, [selectedTemplateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTemplate = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/leads/${lead.id}/render-template/${selectedTemplateId}`, getAuthHeader());
      setMessageData({
        body: response.data.body,
        whatsapp_url: response.data.whatsapp_url
      });
    } catch (error) {
      toast.error('Failed to load template');
    }
  };

  const openWhatsApp = async () => {
    const phone = lead.whatsapp || lead.mobile || '';
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    const result = await sendWhatsApp(cleanPhone, messageData.body);
    if (result.status === 'failed') {
      toast.error(result.error || 'Failed to send');
    } else if (result.mode === 'api') {
      toast.success('Message sent via WhatsApp!');
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-gray-800 mb-4">
          Send WhatsApp to {lead.first_name} {lead.last_name}
        </h3>
        <p className="text-sm text-gray-500 mb-4">{lead.whatsapp || lead.mobile}</p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
          <select
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            {templates.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="h-32 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
            <textarea
              value={messageData.body}
              onChange={(e) => setMessageData(prev => ({ ...prev, body: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              rows={8}
            />
            <p className="text-xs text-gray-500 mt-1">
              Edit the message as needed before sending
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              navigator.clipboard.writeText(messageData.body);
              toast.success('Message copied to clipboard!');
            }}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Copy Text
          </button>
          <button
            onClick={openWhatsApp}
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            Open WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
}
